import React, { useState, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Label,
  Input,
  Table,
  Badge,
  Alert,
} from "reactstrap";

const IELTSScoreCalculator = () => {
  const [scores, setScores] = useState({
    listening: "",
    reading: "",
    writing: "",
    speaking: "",
  });

  const handleChange = (field, value) => {
    if (value === "") {
      setScores({ ...scores, [field]: "" });
      return;
    }
    const num = parseFloat(value);
    if (num >= 0 && num <= 9 && Number.isInteger(num * 2)) {
      setScores({ ...scores, [field]: value });
    }
  };

  const overallBand = useMemo(() => {
    const vals = Object.values(scores).filter(v => v !== "").map(Number);
    if (vals.length !== 4) return null;
    const avg = vals.reduce((a, b) => a + b, 0) / 4;
    return Math.round(avg * 2) / 2;
  }, [scores]);

  const getBandCategory = (band) => {
    if (!band) return { text: "Enter all scores", color: "secondary" };
    if (band >= 8.5) return { text: "Expert User", color: "success" };
    if (band >= 7.0) return { text: "Very Good User", color: "primary" };
    if (band >= 6.0) return { text: "Competent User", color: "info" };
    if (band >= 5.0) return { text: "Modest User", color: "warning" };
    return { text: "Limited User", color: "danger" };
  };

  const category = getBandCategory(overallBand);

  const bandTable = [
    { band: "9.0", level: "Expert User" },
    { band: "8.0 – 8.5", level: "Very Good User" },
    { band: "7.0 – 7.5", level: "Good User" },
    { band: "6.0 – 6.5", level: "Competent User" },
    { band: "5.0 – 5.5", level: "Modest User" },
  ];

  return (
    <div style={{ background: "#f8f9fa", minHeight: "100vh", padding: "2rem 0" }}>
      <Container style={{ maxWidth: "900px" }}>

        {/* Header */}
        <Card className="border-0 shadow-sm mb-4">
          <CardBody
            className="text-center py-4"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            }}
          >
            <h1 className="text-white fw-bold mb-2">
              🎓 IELTS Band Score Calculator
            </h1>
            <p className="text-white mb-0" style={{ opacity: 0.9 }}>
              Calculate your IELTS overall band score accurately
            </p>
          </CardBody>
        </Card>

        {/* Input Section */}
        <Card className="border-0 shadow-sm mb-4">
          <CardBody className="p-4">
            <Label className="fw-bold text-uppercase text-muted mb-3">
              📝 Enter Section Scores
            </Label>

            <Row className="g-3">
              {Object.keys(scores).map((skill) => (
                <Col md={6} key={skill}>
                  <FormGroup>
                    <Label className="fw-semibold text-capitalize">
                      {skill}
                    </Label>
                    <Input
                      type="number"
                      step="0.5"
                      placeholder="0 – 9"
                      value={scores[skill]}
                      onChange={(e) => handleChange(skill, e.target.value)}
                      className="py-2"
                    />
                  </FormGroup>
                </Col>
              ))}
            </Row>

            <div className="mt-3 p-3 bg-light rounded border-start border-4 border-primary">
              <small className="text-muted">
                IELTS overall band is the average of four modules, rounded to the
                nearest 0.5.
              </small>
            </div>
          </CardBody>
        </Card>

        {/* Result Section */}
        <Card className="border-0 shadow-sm mb-4">
          <CardBody className="p-4">
            <Label className="fw-bold text-uppercase text-muted mb-3">
              Your Result
            </Label>

            <Alert
              color={category.color}
              className="border-0 mb-0"
              style={{ background: overallBand ? undefined : "#e9ecef" }}
            >
              <Row className="align-items-center">
                <Col md={6} className="text-center text-md-start mb-3 mb-md-0">
                  <div className="text-uppercase fw-semibold mb-1 small">
                    Overall Band Score
                  </div>
                  <div style={{ fontSize: "2.5rem", fontWeight: "700" }}>
                    {overallBand ?? "0.0"}
                  </div>
                  <Badge color={category.color} className="mt-2">
                    {category.text}
                  </Badge>
                </Col>

                <Col md={6} className="jsutify-content-end">
                  <Row className="g-3 ">
                    <Col lg={8} >
                      <div className="text-center bg-white rounded p-3">
                        <div className="text-muted small">Sections</div>
                        <div className="fw-bold fs-5">
                          {Object.values(scores).filter(v => v !== "").length}/4
                        </div>
                      </div>
                    </Col>
                    <Col xs={6}>
                      <div className="text-center bg-white rounded p-3">
                        <div className="text-muted small">Accuracy</div>
                        <div className="fw-bold fs-5">
                          {overallBand ? "Official" : "--"}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Alert>
          </CardBody>
        </Card>

        {/* Reference Table */}
        <Card className="border-0 shadow-sm">
          <CardBody className="p-4">
            <Label className="fw-bold text-uppercase text-muted mb-3">
              📋 IELTS Band Descriptors
            </Label>

            <div className="table-responsive">
              <Table hover className="align-middle mb-0">
                <thead className="bg-light">
                  <tr>
                    <th>Band</th>
                    <th className="text-end">User Level</th>
                  </tr>
                </thead>
                <tbody>
                  {bandTable.map((row, i) => (
                    <tr key={i}>
                      <td className="fw-bold">{row.band}</td>
                      <td className="text-end text-muted">{row.level}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>

        {/* Footer */}
        <div className="text-center mt-4">
          <p className="text-muted small mb-0">
            <strong>IELTS Band Calculator</strong> • Official Rounding System
          </p>
        </div>

      </Container>
    </div>
  );
};

export default IELTSScoreCalculator;
